import * as THREE from 'three';
import { buildMuseumPavilion } from './museumPavilionGeometry';
import { paintGalleryMark } from './galleryIdentity';
import { archivePointPosition, loadMuseumArchivePoints } from './museumArchive';
import { createMuseumIdentityPanel } from './museumIdentityPanel';

// The portfolio model uses the pavilion's real dimensions and furniture
// positions. Its moving roof is independent of the live Gallery scene.
export function createGalleryArchitecturalModel(materials, invalidate, detailed = false) {
  const root = new THREE.Group(), roof = new THREE.Group();
  const textures = new Set(), ownMaterials = new Set();
  let disposed = false;
  root.add(roof);
  const { edge, stone, paper, metal } = materials;
  function box(w,h,d,material,x,y,z,parent=root) {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w,h,d),material);
    mesh.position.set(x,y,z); parent.add(mesh); return mesh;
  }
  buildMuseumPavilion((w,h,d,m,x,y,z) => {
    const isRoof = y > 4.7 && h < .5;
    let material = m;
    if (isRoof) { material = m.clone(); material.transparent = true; ownMaterials.add(material); }
    return box(w,h,d,material,x,y,z,isRoof ? roof : root);
  }, materials);

  function surface(w,h,x,y,z,parent=root,rotation=0) {
    const material = new THREE.MeshStandardMaterial({color:'#faf7f0',roughness:1,side:THREE.DoubleSide});
    ownMaterials.add(material);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w,h),material);
    mesh.position.set(x,y,z); mesh.rotation.y = rotation; parent.add(mesh); return mesh;
  }
  function print(mesh,draw,w=1024,h=512) {
    const canvas = document.createElement('canvas'); canvas.width=w; canvas.height=h;
    draw(canvas.getContext('2d'),w,h);
    const texture = new THREE.CanvasTexture(canvas); texture.colorSpace=THREE.SRGBColorSpace;
    textures.add(texture); mesh.material.map=texture; mesh.material.needsUpdate=true;
  }
  function photograph(mesh,url) {
    new THREE.TextureLoader().load(url,texture=>{
      if(disposed){texture.dispose();return;}
      texture.colorSpace=THREE.SRGBColorSpace; textures.add(texture);
      mesh.material.map=texture; mesh.material.color.set('#ffffff'); mesh.material.needsUpdate=true; invalidate();
    });
  }
  const cover='/assets/view-covers/8589e24f-df1b-4c45-9d37-fdcadc97e00f-384.webp';

  // Browse display, with the same landscape mounts as the live catalogue.
  const board=new THREE.Group();board.position.set(-11.05,2.7,5.7);board.rotation.y=Math.PI/2;root.add(board);
  box(6.5,4.65,.16,stone,0,0,0,board);
  const boardFace=surface(6.1,4.3,0,0,.085,board);
  print(boardFace,(c,w,h)=>{c.fillStyle='#f6f1e6';c.fillRect(0,0,w,h);c.fillStyle='#514b40';c.font='30px Georgia';c.fillText('The collection',50,52);c.font='68px Georgia';c.fillText('Views',50,130);c.font='20px sans-serif';c.fillText('ON VIEW',60,272);},1024,768);
  if(detailed){
    photograph(surface(4.4,1.1,0,.69,.12,board),cover);
    const covers=['8589e24f-df1b-4c45-9d37-fdcadc97e00f','64193ede-845e-4fcd-a1a4-99a3f19c372b','8b34df01-2485-41f0-bdda-7110a5787a33','62577cd6-62b5-4066-96e4-5c82380c3b06'];
    covers.forEach((id,i)=>photograph(surface(2.6,.65,(i%2?1:-1)*1.43,-.68-Math.floor(i/2)*1.01,.12,board),`/assets/view-covers/${id}-384.webp`));
  }
  // The freestanding Gallery sign.
  box(1.12,.08,.66,edge,8.3,.04,5.4);box(.86,3.35,.10,metal,8.3,1.755,5.4);
  print(surface(.82,3.043,8.3,1.78,5.452),(c,w,h)=>{
    c.fillStyle='#3f403b';c.fillRect(0,0,w,h);c.fillStyle='#f5efe4';paintGalleryMark(c,65,94,100);
    c.font='60px Georgia';c.fillText('The',65,338);c.font='110px Georgia';c.fillText('View',60,472);c.fillText('Gallery',60,589);
    c.globalAlpha=.7;c.fillRect(65,1370,380,2);c.font='42px sans-serif';c.fillText('Browse',65,1502);c.fillText('Entrance',65,1672);
  },512,1900);
  // Compose pedestal and the open portfolio.
  box(2.45,.14,1.6,edge,-6.7,.07,-1.2);box(2.1,1.08,1.3,stone,-6.7,.65,-1.2);
  box(1.9,2.35,.09,metal,-6.7,2.49,-1.2);box(1.81,2.26,.065,paper,-6.7,2.49,-1.123);
  print(surface(1.79,2.2375,-6.7,2.49,-1.084),(c,w,h)=>{
    c.fillStyle='#fbf7ed';c.fillRect(0,0,w,h);c.fillStyle='#70685e';c.font='44px Georgia';c.fillText('A question of your own',65,160);
    c.strokeStyle='#b8ad9b';c.lineWidth=2;for(let i=0;i<5;i++){c.beginPath();c.moveTo(65,310+i*140);c.lineTo(800-i%2*170,310+i*140);c.stroke();}
  },1000,1250);
  const bookCover=box(1.9,2.35,.055,metal,-8.3,2.49,-.725);bookCover.rotation.y=-Math.PI/6;
  // The model table opposite the portfolio, with its miniature footprint.
  box(3.5,.14,3.7,edge,6.7,.07,-1.2);box(3.4,1.08,3.6,stone,6.7,.68,-1.2);box(4.1,.08,4.4,paper,6.7,1.3,-1.2);
  const miniature=new THREE.Group();miniature.position.set(6.45,1.37,-.4);miniature.scale.setScalar(.065);root.add(miniature);
  buildMuseumPavilion((...args)=>box(...args,miniature),materials);
  // The selected exhibition at the back of the Hall.
  box(6.3,1.725,.09,paper,0,3.12,-13.6);
  if(detailed)photograph(surface(6.1,1.525,0,3.12,-13.535),cover);
  print(surface(6.2,1.666,0,1.18,-13.54),(c,w,h)=>{c.fillStyle='#e0d9c9';c.fillRect(0,0,w,h);c.fillStyle='#514b40';c.textAlign='center';c.font='62px Georgia';c.fillText('The Places That',w/2,170);c.fillText('Shaped Henry',w/2,250);},1024,430);
  // Archive installation and the portrait on the reading court wall.
  const identityPanel = createMuseumIdentityPanel(root);
  for(const [r,h,y,m] of [[2.45,.12,.06,edge],[1.92,.73,.47,stone],[2.18,.045,.85,metal],[2.2,.09,.935,paper],[1.73,.014,.997,edge]]){
    const mesh=new THREE.Mesh(new THREE.CylinderGeometry(r,r,h,48),m);mesh.position.set(16.7,y,3.4);root.add(mesh);
  }
  const portrait=new THREE.Group();portrait.position.set(22.325,2.45,3.4);portrait.rotation.y=-Math.PI/2;root.add(portrait);
  box(1.86,2.8,.065,edge,0,0,0,portrait);box(1.83,2.77,.018,paper,0,0,.042,portrait);
  if(detailed){
    photograph(surface(1.62,1.62*1404/1011,0,.13,.054,portrait),'/assets/img/henry-rooftop-portrait.jpg');
    loadMuseumArchivePoints().then(points=>{
      if(disposed)return;
      const positions=points.map(archivePointPosition),radius=Math.max(.001,...positions.map(p=>Math.hypot(...p))),scale=Math.min(1,1.9/radius);
      const vertices=positions.flatMap(([x,y,z])=>[16.7+x*scale,2.42+y*scale,3.4+z*scale]);
      const material=new THREE.PointsMaterial({color:'#87745b',size:.07,sizeAttenuation:true});ownMaterials.add(material);
      root.add(new THREE.Points(new THREE.BufferGeometry().setAttribute('position',new THREE.Float32BufferAttribute(vertices,3)),material));invalidate();
    }).catch(()=>{});
  }
  return {root,roof,dispose(){disposed=true;identityPanel.dispose();textures.forEach(t=>t.dispose());ownMaterials.forEach(m=>m.dispose());root.traverse(o=>o.geometry?.dispose());}};
}
